import React, { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import AxosShell from "./AxosShell";
import { Block, useAnno } from "./Annotate";
import { QUIZ_PINS } from "./tests";
import { A, SERIF, MONO, QUIZ_URL, SHOTS } from "./brand";

const QUESTIONS = [
  {
    id: "goal",
    q: "What are you trying to improve first?",
    help: "Pick the one that annoys you most day to day.",
    options: ["Energy through the afternoon", "Brain fog and focus", "Digestion and bloating", "Hair, skin and nails"],
  },
  {
    id: "status",
    q: "Where are you with a diagnosis?",
    help: "This decides which routine we can recommend.",
    options: ["Diagnosed Hashimoto's", "Diagnosed hypothyroid", "Waiting on results", "No diagnosis, symptoms only"],
  },
  {
    id: "meds",
    q: "Are you taking thyroid medication?",
    help: "We will time the capsule around it.",
    options: ["Levothyroxine", "Liothyronine or NDT", "Nothing right now", "Prefer not to say"],
  },
];

export default function AxosQuiz() {
  return (
    <AxosShell pins={QUIZ_PINS} kicker="Axos · three step quiz">
      <QuizBody />
    </AxosShell>
  );
}

function QuizBody() {
  const anno = useAnno();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");

  /* opening a pin for a later screen jumps the quiz to that screen */
  useEffect(() => {
    const map = { "quiz-intro": 0, "quiz-q1": 0, "quiz-progress": 1, "quiz-email": 3, "quiz-result": 4 };
    if (anno?.active && map[anno.active] !== undefined) setStep(map[anno.active]);
  }, [anno?.active]);

  const pick = (id, v) => {
    setAnswers((a) => ({ ...a, [id]: v }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const pct = step >= 3 ? 100 : Math.round(((step + 1) / 3) * 100);

  return (
    <div className="min-h-screen px-5 py-10 sm:px-10" style={{ background: A.cream, color: A.body }}>
      <div className="mx-auto max-w-[680px]">
        <div className="mb-8 text-center">
          <div className="text-[22px]" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 600 }}>
            axos
          </div>
          <Block id="quiz-intro" className="mx-auto mt-3 inline-block px-3 py-1">
            <span className="text-[12.5px]" style={{ color: A.soft }}>
              Three questions. About sixty seconds.
            </span>
          </Block>
        </div>

        {step < 3 && (
          <Block id="quiz-progress" className="mb-8">
            <div className="h-[6px] w-full overflow-hidden rounded-full" style={{ background: A.sand }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${A.teal}, ${A.blush})` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[11px]" style={{ fontFamily: MONO, color: A.soft }}>
              <span>{pct}% done</span>
              <span>about {3 - step === 3 ? 60 : (3 - step) * 20} seconds left</span>
            </div>
          </Block>
        )}

        {step < 3 &&
          (() => {
            const q = QUESTIONS[step];
            const body = (
              <div
                className="rounded-[22px] border p-6 sm:p-8"
                style={{ borderColor: A.line, background: A.white, boxShadow: "0 18px 44px rgba(14,33,64,0.07)" }}
              >
                <div className="mb-2 text-[10px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.teal }}>
                  Question {step + 1} of 3
                </div>
                <h1 className="text-4xl leading-[1.1] sm:text-5xl" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 500, fontSize: 32 }}>
                  {q.q}
                </h1>
                <p className="mt-2 text-[13px]" style={{ color: A.soft }}>
                  {q.help}
                </p>
                <div className="mt-6 grid gap-2.5">
                  {q.options.map((o) => {
                    const live = answers[q.id] === o;
                    return (
                      <button
                        key={o}
                        type="button"
                        data-testid={`quiz-option-${o.split(" ")[0].toLowerCase()}`}
                        onClick={() => pick(q.id, o)}
                        className="flex items-center justify-between gap-3 rounded-xl border px-4 py-3.5 text-left text-[14.5px] transition-colors"
                        style={{
                          borderColor: live ? A.blush : A.line,
                          background: live ? "rgba(233,140,158,0.10)" : A.white,
                          color: A.navy,
                        }}
                      >
                        {o}
                        <span
                          className="grid h-[20px] w-[20px] shrink-0 place-items-center rounded-full"
                          style={{ border: `1.5px solid ${live ? A.blush : A.line}`, background: live ? A.blush : "transparent" }}
                        >
                          {live && <Check size={12} color={A.white} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {step > 0 && (
                  <button
                    type="button"
                    data-testid="quiz-back"
                    onClick={() => setStep(step - 1)}
                    className="mt-5 inline-flex items-center gap-2 text-[12.5px]"
                    style={{ color: A.soft }}
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                )}
              </div>
            );
            return step === 0 ? <Block id="quiz-q1">{body}</Block> : body;
          })()}

        {step === 3 && (
          <Block id="quiz-email">
            <div
              className="rounded-[22px] border p-6 text-center sm:p-8"
              style={{ borderColor: A.line, background: A.white, boxShadow: "0 18px 44px rgba(14,33,64,0.07)" }}
            >
              <div className="mb-2 text-[10px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.teal }}>
                Your match is ready
              </div>
              <h1 style={{ fontFamily: SERIF, color: A.navy, fontWeight: 500, fontSize: 30, lineHeight: 1.15 }}>
                Your match is Hashi-aid. Want it saved and your offer sent?
              </h1>
              <p className="mx-auto mt-3 max-w-[420px] text-[13.5px]" style={{ color: A.body }}>
                Skip this if you would rather just see it. Nothing is hidden behind the form.
              </p>
              <div className="mx-auto mt-6 flex max-w-[420px] flex-col gap-2.5 sm:flex-row">
                <input
                  data-testid="quiz-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="flex-1 rounded-full border px-4 py-3 text-[14px] outline-none"
                  style={{ borderColor: A.line, color: A.navy, background: A.cream }}
                />
                <button
                  type="button"
                  data-testid="quiz-email-submit"
                  onClick={() => setStep(4)}
                  className="rounded-full px-6 py-3 text-[14px] font-medium"
                  style={{ background: A.blush, color: A.white }}
                >
                  Save it
                </button>
              </div>
              <button
                type="button"
                data-testid="quiz-email-skip"
                onClick={() => setStep(4)}
                className="mt-4 text-[12.5px] underline"
                style={{ color: A.soft }}
              >
                Show my match without saving
              </button>
            </div>
          </Block>
        )}

        {step === 4 && (
          <Block id="quiz-result">
            <div className="overflow-hidden rounded-[22px] border" style={{ borderColor: A.line, background: A.white }}>
              <div className="px-6 py-7 text-center sm:px-10" style={{ background: A.navy }}>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.tealSoft }}>
                  Your match
                </div>
                <h1 className="mt-2" style={{ fontFamily: SERIF, color: A.white, fontWeight: 500, fontSize: 32 }}>
                  Hashi-aid
                </h1>
                <p className="mx-auto mt-2 max-w-[440px] text-[13.5px]" style={{ color: "#C3CEDC" }}>
                  Based on {answers.goal ? answers.goal.toLowerCase() : "your answer"}, {" "}
                  {answers.status ? answers.status.toLowerCase() : "your diagnosis"} and{" "}
                  {answers.meds ? answers.meds.toLowerCase() : "your medication"}.
                </p>
              </div>
              <div className="grid items-center gap-6 p-6 sm:grid-cols-[0.8fr_1.2fr] sm:p-8">
                <img src={SHOTS.hashi} alt="Hashi-aid" className="mx-auto w-full max-w-[220px]" />
                <div>
                  <div className="text-[14px]" style={{ color: A.navy, fontWeight: 600 }}>
                    Your routine, delivered monthly, 15% off every order
                  </div>
                  <ul className="mt-3 grid gap-2 text-[13.5px]" style={{ color: A.body }}>
                    {[
                      "One capsule a day, taken four hours from your medication",
                      "Selenium, zinc, vitamin D3 with K2, methylated B12",
                      "Cancel or pause from the first email, no phone call",
                    ].map((l) => (
                      <li key={l} className="flex gap-2.5">
                        <Check size={15} color={A.teal} className="mt-[3px] shrink-0" />
                        {l}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={QUIZ_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="quiz-result-cta"
                    className="mt-6 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-[14.5px] font-medium"
                    style={{ background: A.blush, color: A.white }}
                  >
                    Unlock my offer
                    <ArrowRight size={16} />
                  </a>
                  <div className="mt-3 text-[11.5px]" style={{ color: A.soft }}>
                    Continues on quiz.ibdassist.com
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              data-testid="quiz-restart"
              onClick={() => {
                setStep(0);
                setAnswers({});
              }}
              className="mx-auto mt-6 block text-[12.5px] underline"
              style={{ color: A.soft }}
            >
              Start again
            </button>
          </Block>
        )}
      </div>
    </div>
  );
}
